import {
  Component,
  computed,
  AfterViewInit,
  OnDestroy,
  AfterViewChecked,
  effect,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { AuthService } from '../../services/auth/auth.service';

import { NavItem } from '../../models/navItem';
import { TournamentService } from '../../services';
import { LayoutService } from '../../services/shared/layout/layout.service';

declare var $: any; // jQuery declaration for metisMenu

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
})
export class NavbarComponent
  implements AfterViewInit, OnDestroy, AfterViewChecked
{
  userName = this.authService.userName;
  userRole = this.authService.userRole;

  isAuthenticated = this.authService.isAuthenticated;

  selectedTournament = computed(() =>
    this.tournamentService.selectedTournamentSignal()
  );
  selectedRound = computed(() => this.tournamentService.selectedRoundSignal());

  items = computed(() => {
    const baseItems = this.authService.getNavItems() || [];
    const tournamentId = this.selectedTournament()?.tournamentId;
    const roundId = this.selectedRound()?.roundId;
    const roundNumber = this.selectedRound()?.number!.toString();

    const itemsWithTokens = this.replaceNavItemTokens(
      baseItems,
      tournamentId,
      roundId,
      roundNumber
    );
    return this.applyNavState(itemsWithTokens);
  });

  private metisMenuInitialized = false;
  private readonly unresolvedNavTokens = [':tournamentId', ':roundId'];

  constructor(
    private authService: AuthService,
    private tournamentService: TournamentService,
    private layoutService: LayoutService
  ) {
    effect(() => {
      if (this.layoutService.isSidebarCollapsed()) {
        this.collapseExpandedMenus();
      }
    });
  }

  private applyNavState(items: NavItem[]): NavItem[] {
    // No longer disabling Simulator link based on active tournament
    return items;
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.initializeMetisMenu(), 100);
  }

  ngAfterViewChecked(): void {
    // Reinitialize if not already done and DOM is ready
    if (!this.metisMenuInitialized && this.isAuthenticated()) {
      setTimeout(() => this.initializeMetisMenu(), 50);
    }
  }

  ngOnDestroy(): void {
    this.destroyMetisMenu();
  }

  private initializeMetisMenu(): void {
    if (typeof $ !== 'undefined' && $('#side-menu').length > 0) {
      try {
        // Destroy existing instance
        this.destroyMetisMenu();

        // DON'T initialize metisMenu - handle manually instead
        // This prevents conflicts with our manual click handling

        this.metisMenuInitialized = true;
        console.log('Navigation initialized for manual handling');
      } catch (error) {
        console.error('Error initializing Navigation:', error);
      }
    }
  }

  private destroyMetisMenu(): void {
    if (typeof $ !== 'undefined' && $('#side-menu').length > 0) {
      try {
        // Remove existing click handlers
        $('#side-menu').off('click.metisMenu');
        $('#side-menu').off('click');
        this.metisMenuInitialized = false;
      } catch (error) {
        console.error('Error destroying Navigation:', error);
      }
    }
  }

  // Handle expandable item clicks manually
  onExpandableItemClick(event: Event, item: NavItem): void {
    if (this.canExpand(item)) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const target = event.currentTarget as HTMLElement;
      const li = target.closest('li');
      const submenu = li?.querySelector('ul') as HTMLElement;

      if (li && submenu) {
        const isCurrentlyExpanded = li.classList.contains('active');

        if (isCurrentlyExpanded) {
          // Collapse
          li.classList.remove('active');
          li.classList.remove('nav-expanded'); // Remove custom class for styling
          submenu.classList.remove('in');
          submenu.classList.add('collapse');
        } else {
          // Expand
          li.classList.add('active');
          li.classList.add('nav-expanded'); // Add custom class for styling expanded state
          submenu.classList.remove('collapse');
          submenu.classList.add('in');
        }

        console.log(
          'Toggle menu:',
          isCurrentlyExpanded ? 'collapsed' : 'expanded'
        );
      }
    } else {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }
  }

  canExpand(item: NavItem | null | undefined): boolean {
    if (!item || !item.children || item.children.length === 0) {
      return false;
    }

    if (!item.url) {
      return false;
    }

    return !this.unresolvedNavTokens.some((token) => item.url.includes(token));
  }

  private replaceNavItemTokens(
    items: NavItem[],
    tournamentId?: string,
    roundId?: string,
    roundNumber?: string
  ): NavItem[] {
    return items.map((item) => ({
      ...item,
      name: item.name.replace(':roundNumber', roundNumber || ''),
      url: this.replaceUrl(item.url, tournamentId, roundId),
      children: item.children
        ? this.replaceNavItemTokens(
            item.children,
            tournamentId,
            roundId,
            roundNumber
          )
        : undefined,
    }));
  }

  private replaceUrl(
    url: string,
    tournamentId?: string,
    roundId?: string
  ): string {
    let replacedUrl = url;
    if (tournamentId) {
      replacedUrl = replacedUrl.replace(':tournamentId', tournamentId);
    }
    if (roundId) {
      replacedUrl = replacedUrl.replace(':roundId', roundId);
    }
    return replacedUrl;
  }

  private collapseExpandedMenus(): void {
    const navElement = document.getElementById('side-menu');
    if (!navElement) {
      return;
    }

    navElement
      .querySelectorAll('li.nav-expanded')
      .forEach((li) => li.classList.remove('nav-expanded'));
    navElement.querySelectorAll('ul.in').forEach((ul) => {
      ul.classList.remove('in');
      ul.classList.add('collapse');
    });
  }
}
